import type {
  AdminLeadAssignee,
  AdminLeadAssigneeDirectoryCursor,
  AdminLeadAssignmentRequest,
  AdminLeadListItem,
  IdempotencyKey,
} from "@whitehaven/contracts";
import { ContentState, FormField, Select, StatusPill } from "@whitehaven/ui";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
} from "react";
import {
  captureProtectedStateGeneration,
  isProtectedStateGenerationCurrent,
  registerProtectedStateCleaner,
} from "../../protected-state";
import {
  ADMIN_LEAD_ASSIGNEE_DIRECTORY_PAGE_SIZE,
  type AdminLeadAssigneeDirectoryGateway,
  type AdminLeadAssignmentMutationGateway,
} from "./lead-assignment-gateway";
import {
  applyConfirmedAdminLeadAssignment,
  isAdminLeadAssigneeDirectoryPageConsistent,
  isAdminLeadAssignmentFailureOutcome,
  isConfirmedAdminLeadAssignmentConsistent,
  leadAssignableRoleLabels,
  mergeAdminLeadAssignees,
  UNASSIGNED_LEAD_VALUE,
} from "./lead-assignment-model";

type DirectoryNotice = "LOAD_MORE_FAILED" | null;

type DirectoryState =
  | { readonly kind: "unavailable" }
  | { readonly kind: "loading" }
  | { readonly kind: "error" }
  | {
      readonly kind: "ready";
      readonly items: readonly AdminLeadAssignee[];
      readonly nextCursor: AdminLeadAssigneeDirectoryCursor | null;
      readonly consumedCursors: readonly AdminLeadAssigneeDirectoryCursor[];
      readonly loadingMore: boolean;
      readonly notice: DirectoryNotice;
    };

interface AssignmentFeedback {
  readonly tone: "positive" | "critical";
  readonly message: string;
  readonly refreshRequired: boolean;
}

interface AdminLeadAssignmentPanelProps {
  readonly lead: AdminLeadListItem;
  readonly directoryGateway: AdminLeadAssigneeDirectoryGateway | null;
  readonly mutationGateway: AdminLeadAssignmentMutationGateway | null;
  readonly detailRefreshing?: boolean;
  readonly externalMutationPending?: boolean;
  readonly refreshEpoch: number;
  readonly onConfirmed: (lead: AdminLeadListItem) => void;
  readonly onPendingChange?: (pending: boolean) => void;
  readonly onRefreshRequired: () => void;
}

function createAssignmentIdempotencyKey(): IdempotencyKey | null {
  try {
    const value = globalThis.crypto?.randomUUID?.();
    return value && /^[A-Za-z0-9_-]{8,128}$/.test(value)
      ? (value as IdempotencyKey)
      : null;
  } catch {
    return null;
  }
}

function currentAssignmentLabel(lead: AdminLeadListItem): string {
  if (lead.assignedUserId === null) return "Unassigned";
  return lead.assignedTo?.displayLabel ?? "Unavailable assignee";
}

function failedDirectoryState(
  current: DirectoryState,
  mode: "initial" | "load-more",
): DirectoryState {
  if (mode === "initial" || current.kind !== "ready") {
    return { kind: "error" };
  }

  return {
    ...current,
    loadingMore: false,
    notice: "LOAD_MORE_FAILED",
  };
}

export function AdminLeadAssignmentPanel({
  lead,
  directoryGateway,
  mutationGateway,
  detailRefreshing = false,
  externalMutationPending = false,
  refreshEpoch,
  onConfirmed,
  onPendingChange,
  onRefreshRequired,
}: AdminLeadAssignmentPanelProps) {
  const canLoadDirectory =
    directoryGateway !== null &&
    mutationGateway !== null &&
    lead.leadRecordStatus === "ACTIVE";
  const [directory, setDirectory] = useState<DirectoryState>(
    canLoadDirectory ? { kind: "loading" } : { kind: "unavailable" },
  );
  const [selectedUserId, setSelectedUserId] = useState<string>(
    lead.assignedUserId ?? UNASSIGNED_LEAD_VALUE,
  );
  const [pending, setPending] = useState(false);
  const [feedback, setFeedback] = useState<AssignmentFeedback | null>(null);
  const directorySequence = useRef(0);
  const activeDirectoryRequest = useRef<AbortController | null>(null);
  const mutationSequence = useRef(0);
  const activeMutation = useRef<AbortController | null>(null);
  const refreshRequiredAtEpoch = useRef<number | null>(null);
  const feedbackRef = useRef<HTMLParagraphElement | null>(null);
  const panelRef = useRef<HTMLElement | null>(null);
  const latestLead = useRef(lead);
  latestLead.current = lead;

  const requestDirectory = useCallback(
    async (
      mode: "initial" | "load-more",
      cursor: AdminLeadAssigneeDirectoryCursor | null,
    ) => {
      if (!canLoadDirectory || !directoryGateway || activeMutation.current) {
        return;
      }

      const requestId = directorySequence.current + 1;
      directorySequence.current = requestId;
      activeDirectoryRequest.current?.abort();

      const controller = new AbortController();
      activeDirectoryRequest.current = controller;
      const protectedStateGeneration = captureProtectedStateGeneration();

      setDirectory((current) => {
        if (mode === "initial" || current.kind !== "ready") {
          return { kind: "loading" };
        }
        return { ...current, loadingMore: true, notice: null };
      });

      try {
        const page = await directoryGateway(
          {
            cursor,
            limit: ADMIN_LEAD_ASSIGNEE_DIRECTORY_PAGE_SIZE,
          },
          { signal: controller.signal },
        );

        if (
          controller.signal.aborted ||
          directorySequence.current !== requestId ||
          !isProtectedStateGenerationCurrent(protectedStateGeneration)
        ) {
          return;
        }

        if (!isAdminLeadAssigneeDirectoryPageConsistent(page)) {
          setDirectory((current) => failedDirectoryState(current, mode));
          return;
        }

        setDirectory((current) => {
          if (
            mode === "load-more" &&
            (cursor === null ||
              current.kind !== "ready" ||
              page.nextCursor === cursor ||
              (page.nextCursor !== null &&
                current.consumedCursors.includes(page.nextCursor)))
          ) {
            return failedDirectoryState(current, mode);
          }

          const items =
            mode === "load-more" && current.kind === "ready"
              ? mergeAdminLeadAssignees(current.items, page.items)
              : mergeAdminLeadAssignees([], page.items);

          if (items === null) {
            return failedDirectoryState(current, mode);
          }

          return {
            kind: "ready",
            items,
            nextCursor: page.nextCursor,
            consumedCursors:
              mode === "load-more" &&
              current.kind === "ready" &&
              cursor !== null
                ? [...current.consumedCursors, cursor]
                : [],
            loadingMore: false,
            notice: null,
          };
        });
      } catch {
        if (
          controller.signal.aborted ||
          directorySequence.current !== requestId ||
          !isProtectedStateGenerationCurrent(protectedStateGeneration)
        ) {
          return;
        }
        setDirectory((current) => failedDirectoryState(current, mode));
      } finally {
        if (activeDirectoryRequest.current === controller) {
          activeDirectoryRequest.current = null;
        }
      }
    },
    [canLoadDirectory, directoryGateway],
  );

  const requireLeadAndDirectoryRefresh = useCallback(
    (message: string) => {
      refreshRequiredAtEpoch.current = refreshEpoch;
      setFeedback({
        tone: "critical",
        message,
        refreshRequired: true,
      });
    },
    [refreshEpoch],
  );

  useEffect(() => {
    const unregisterCleaner = registerProtectedStateCleaner(
      "crm-admin-lead-assignment",
      () => {
        directorySequence.current += 1;
        activeDirectoryRequest.current?.abort();
        activeDirectoryRequest.current = null;
        mutationSequence.current += 1;
        activeMutation.current?.abort();
        activeMutation.current = null;
        refreshRequiredAtEpoch.current = null;
        setPending(false);
        onPendingChange?.(false);
        setFeedback(null);
        setSelectedUserId(UNASSIGNED_LEAD_VALUE);
        setDirectory(
          canLoadDirectory ? { kind: "loading" } : { kind: "unavailable" },
        );
      },
    );

    if (canLoadDirectory) {
      void requestDirectory("initial", null);
    } else {
      directorySequence.current += 1;
      activeDirectoryRequest.current?.abort();
      activeDirectoryRequest.current = null;
      setDirectory({ kind: "unavailable" });
    }

    return () => {
      unregisterCleaner();
      directorySequence.current += 1;
      activeDirectoryRequest.current?.abort();
      activeDirectoryRequest.current = null;
      mutationSequence.current += 1;
      activeMutation.current?.abort();
      activeMutation.current = null;
      setPending(false);
      onPendingChange?.(false);
    };
  }, [canLoadDirectory, onPendingChange, requestDirectory]);

  useEffect(() => {
    if (activeMutation.current) return;
    setSelectedUserId(lead.assignedUserId ?? UNASSIGNED_LEAD_VALUE);
    const requiredAt = refreshRequiredAtEpoch.current;
    if (requiredAt !== null && refreshEpoch > requiredAt) {
      refreshRequiredAtEpoch.current = null;
      panelRef.current?.focus();
      setFeedback(null);
      if (canLoadDirectory) {
        void requestDirectory("initial", null);
      }
    }
  }, [
    canLoadDirectory,
    lead.assignedUserId,
    lead.assignmentRevision,
    lead.updatedAt,
    requestDirectory,
    refreshEpoch,
  ]);

  useEffect(() => {
    if (feedback) {
      feedbackRef.current?.focus();
    }
  }, [feedback]);

  const submitAssignment = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (
      !mutationGateway ||
      directory.kind !== "ready" ||
      directory.loadingMore ||
      activeMutation.current ||
      feedback?.refreshRequired === true ||
      detailRefreshing ||
      externalMutationPending ||
      lead.leadRecordStatus !== "ACTIVE"
    ) {
      return;
    }

    const targetUserId =
      selectedUserId === UNASSIGNED_LEAD_VALUE ? null : selectedUserId;
    if (targetUserId === lead.assignedUserId) return;

    const target =
      targetUserId === null
        ? null
        : directory.items.find((item) => item.userId === targetUserId);
    const idempotencyKey = createAssignmentIdempotencyKey();
    if (target === undefined || !idempotencyKey) {
      requireLeadAndDirectoryRefresh(
        "The assignment change could not be prepared. Refresh the lead before trying again.",
      );
      return;
    }

    const request: AdminLeadAssignmentRequest = {
      leadId: lead.leadId,
      expectedAssigneeUserId: lead.assignedUserId,
      expectedAssignmentRevision: lead.assignmentRevision,
      expectedUpdatedAt: lead.updatedAt,
      targetAssigneeUserId: targetUserId,
    };
    const requestId = mutationSequence.current + 1;
    mutationSequence.current = requestId;
    const controller = new AbortController();
    activeMutation.current = controller;
    const protectedStateGeneration = captureProtectedStateGeneration();
    setPending(true);
    onPendingChange?.(true);
    setFeedback(null);

    try {
      const outcome = await mutationGateway(request, {
        signal: controller.signal,
        idempotencyKey,
      });

      if (
        controller.signal.aborted ||
        mutationSequence.current !== requestId ||
        !isProtectedStateGenerationCurrent(protectedStateGeneration)
      ) {
        return;
      }

      const currentLead = latestLead.current;
      if (
        currentLead.leadId !== request.leadId ||
        currentLead.assignedUserId !== request.expectedAssigneeUserId ||
        currentLead.assignmentRevision !== request.expectedAssignmentRevision ||
        currentLead.updatedAt !== request.expectedUpdatedAt
      ) {
        setSelectedUserId(currentLead.assignedUserId ?? UNASSIGNED_LEAD_VALUE);
        setFeedback({
          tone: "critical",
          message:
            "Lead details changed while the assignment was pending. Review the current assignment before trying again.",
          refreshRequired: false,
        });
        return;
      }

      if (isAdminLeadAssignmentFailureOutcome(outcome)) {
        requireLeadAndDirectoryRefresh(
          outcome.kind === "conflict"
            ? "This lead changed elsewhere. Refresh to confirm its assignment."
            : "The assignment change was not confirmed. Refresh before trying again.",
        );
        return;
      }

      if (
        !isConfirmedAdminLeadAssignmentConsistent(
          lead,
          target,
          request,
          outcome,
        )
      ) {
        requireLeadAndDirectoryRefresh(
          "The assignment response could not be confirmed. Refresh the lead.",
        );
        return;
      }

      const updatedLead = applyConfirmedAdminLeadAssignment(
        lead,
        target,
        request,
        outcome,
      );
      if (!updatedLead) {
        requireLeadAndDirectoryRefresh(
          "The confirmed assignment could not be reconciled. Refresh the lead.",
        );
        return;
      }

      onConfirmed(updatedLead);
      refreshRequiredAtEpoch.current = null;
      setSelectedUserId(updatedLead.assignedUserId ?? UNASSIGNED_LEAD_VALUE);
      setFeedback({
        tone: "positive",
        message:
          updatedLead.assignedUserId === null
            ? "The lead is now unassigned."
            : "The lead assignment was confirmed.",
        refreshRequired: false,
      });
    } catch {
      if (
        controller.signal.aborted ||
        mutationSequence.current !== requestId ||
        !isProtectedStateGenerationCurrent(protectedStateGeneration)
      ) {
        return;
      }

      requireLeadAndDirectoryRefresh(
        "The assignment change could not be confirmed. Refresh before trying again.",
      );
    } finally {
      if (
        mutationSequence.current === requestId &&
        isProtectedStateGenerationCurrent(protectedStateGeneration)
      ) {
        activeMutation.current = null;
        setPending(false);
        onPendingChange?.(false);
      }
    }
  };

  const currentUserMissing =
    lead.assignedUserId !== null &&
    directory.kind === "ready" &&
    !directory.items.some((item) => item.userId === lead.assignedUserId);
  const selectionChanged =
    (selectedUserId === UNASSIGNED_LEAD_VALUE ? null : selectedUserId) !==
    lead.assignedUserId;
  const controlsDisabled =
    pending ||
    directory.kind !== "ready" ||
    directory.loadingMore ||
    feedback?.refreshRequired === true ||
    detailRefreshing ||
    externalMutationPending ||
    lead.leadRecordStatus !== "ACTIVE";

  return (
    <section
      ref={panelRef}
      className="crm-lead-detail__card crm-lead-assignment"
      aria-labelledby="crm-lead-assignment-title"
      tabIndex={-1}
      aria-busy={
        pending ||
        directory.kind === "loading" ||
        (directory.kind === "ready" && directory.loadingMore)
          ? "true"
          : undefined
      }
    >
      <div className="crm-lead-assignment__heading">
        <div>
          <h3 id="crm-lead-assignment-title">Assignment</h3>
          <p>
            Assign this lead only to an eligible administrator or CRM agent.
          </p>
        </div>
        <StatusPill tone={lead.assignedUserId === null ? "warning" : "neutral"}>
          {currentAssignmentLabel(lead)}
        </StatusPill>
      </div>

      {lead.assignedTo && (
        <p className="crm-lead-assignment__current-role">
          Current role: {leadAssignableRoleLabels[lead.assignedTo.role]}
        </p>
      )}
      {lead.assignedUserId !== null && lead.assignedTo === null && (
        <p className="crm-lead-assignment__scope-note">
          The recorded assignee is no longer available in the eligible
          directory. The opaque assignment remains recorded until a confirmed
          reassignment or unassignment.
        </p>
      )}

      {directory.kind === "unavailable" && (
        <ContentState
          kind="warning"
          headingLevel={4}
          title={
            lead.leadRecordStatus !== "ACTIVE"
              ? "Archived lead assignment is read-only."
              : "Assignment controls are unavailable."
          }
          live
        >
          <p>
            {lead.leadRecordStatus !== "ACTIVE"
              ? "The recorded assignment remains visible, but eligible staff are not requested for archived leads."
              : mutationGateway === null
                ? "The authorized mutation service has not been connected, so eligible staff are not requested."
                : "The protected assignee directory has not been connected. The current confirmed assignment remains visible."}
          </p>
        </ContentState>
      )}

      {directory.kind === "loading" && (
        <ContentState
          kind="loading"
          headingLevel={4}
          title="Loading eligible assignees."
          live
        />
      )}

      {directory.kind === "error" && (
        <ContentState
          kind="error"
          headingLevel={4}
          title="Eligible assignees could not be loaded."
          live
          actions={
            <button
              type="button"
              className="crm-lead-detail__button"
              disabled={pending}
              onClick={() => {
                panelRef.current?.focus();
                void requestDirectory("initial", null);
              }}
            >
              Try directory again
            </button>
          }
        >
          <p>No assignment change is available until the directory loads.</p>
        </ContentState>
      )}

      {canLoadDirectory && directory.kind === "ready" && (
        <form className="crm-lead-assignment__form" onSubmit={submitAssignment}>
          <FormField
            id="crm-lead-assignee"
            label="Eligible assignee"
            hint="Only active administrators and CRM agents with approved CRM access appear."
          >
            {(controlProps) => (
              <Select
                {...controlProps}
                value={selectedUserId}
                disabled={controlsDisabled}
                onChange={(event) => {
                  setSelectedUserId(event.currentTarget.value);
                  setFeedback(null);
                }}
              >
                <option value={UNASSIGNED_LEAD_VALUE}>Unassigned</option>
                {currentUserMissing && lead.assignedUserId && (
                  <option value={lead.assignedUserId} disabled>
                    {lead.assignedTo?.displayLabel ??
                      "Current assignee unavailable"}{" "}
                    · Current recorded assignment
                  </option>
                )}
                {directory.items.map((assignee) => (
                  <option key={assignee.userId} value={assignee.userId}>
                    {assignee.displayLabel} ·{" "}
                    {leadAssignableRoleLabels[assignee.role]}
                  </option>
                ))}
              </Select>
            )}
          </FormField>

          {!mutationGateway && (
            <p className="crm-lead-assignment__scope-note">
              Assignment changes require the authorized mutation service.
            </p>
          )}
          {lead.leadRecordStatus !== "ACTIVE" && (
            <p className="crm-lead-assignment__scope-note">
              Archived leads retain their recorded assignment and cannot be
              changed.
            </p>
          )}

          <div className="crm-lead-assignment__actions">
            <button
              type="submit"
              className="crm-lead-detail__button"
              disabled={
                controlsDisabled || !selectionChanged || !mutationGateway
              }
            >
              {pending ? "Confirming assignment" : "Confirm assignment"}
            </button>
            {feedback?.refreshRequired && (
              <button
                type="button"
                className="crm-lead-detail__button crm-lead-detail__button--secondary"
                onClick={onRefreshRequired}
              >
                Refresh lead details
              </button>
            )}
          </div>

          {feedback && (
            <p
              ref={feedbackRef}
              className={`crm-lead-assignment__feedback crm-lead-assignment__feedback--${feedback.tone}`}
              role={feedback.tone === "critical" ? "alert" : "status"}
              aria-live={feedback.tone === "critical" ? "assertive" : "polite"}
              aria-atomic="true"
              tabIndex={-1}
            >
              {feedback.message}
            </p>
          )}

          {directory.notice && (
            <ContentState
              kind="error"
              headingLevel={4}
              title="More eligible assignees could not be loaded."
              live
              actions={
                <button
                  type="button"
                  className="crm-lead-detail__button"
                  disabled={pending}
                  onClick={() => {
                    panelRef.current?.focus();
                    if (directory.nextCursor) {
                      void requestDirectory("load-more", directory.nextCursor);
                    }
                  }}
                >
                  Try again
                </button>
              }
            >
              <p>
                The confirmed assignee options already loaded remain usable.
              </p>
            </ContentState>
          )}

          {directory.nextCursor && (
            <button
              type="button"
              className="crm-lead-detail__button crm-lead-detail__button--secondary"
              disabled={pending || directory.loadingMore}
              onClick={() =>
                void requestDirectory("load-more", directory.nextCursor)
              }
            >
              {directory.loadingMore
                ? "Loading assignees"
                : "Load more eligible assignees"}
            </button>
          )}
        </form>
      )}
    </section>
  );
}
